import { Info, TriangleAlert } from "lucide-react";
import {
  formatCoverage,
  formatUnfinished,
  type AnswerCoverage,
  type AnswerUnfinished,
} from "@gb/contracts";
import { DISPLAY_LOCALE } from "@/lib/format";

/**
 * The two lines under an answer (2026-09-26, Phase 5; §3 of the design,
 * `document-ai/0030` §2.4.2):
 *
 * - **how it stopped**, in the warning colour, when it stopped before it
 *   finished: "Unfinished: stopped after 100 steps. Read 18 of 42 files in
 *   “Variation orders”." An answer with this line gets no notice in its text
 *   as well, so the reader is not told twice;
 * - **what it read against what there was**, muted, with an info icon: "Read 4
 *   of 135 files. 87 hidden."
 *
 * Code writes both, from the stored counts, in the reader's locale
 * (`formatUnfinished`, `formatCoverage`). An answer made before the agent has
 * neither, and shows nothing here.
 */
export function AnswerLines({
  coverage,
  unfinished,
}: {
  coverage: AnswerCoverage | null;
  unfinished: AnswerUnfinished | null;
}) {
  if (!coverage && !unfinished) return null;
  return (
    <div className="flex flex-col gap-1.5">
      {unfinished && (
        <p className="flex items-start gap-1.5 text-[0.82rem] font-semibold text-warn">
          <TriangleAlert size={14} className="mt-[3px] shrink-0" aria-hidden />
          <span>{formatUnfinished(unfinished, DISPLAY_LOCALE)}</span>
        </p>
      )}
      {coverage && (
        <p className="flex items-start gap-1.5 text-[0.8rem] text-muted">
          <Info size={14} className="mt-[3px] shrink-0" aria-hidden />
          <span>{formatCoverage(coverage, DISPLAY_LOCALE)}</span>
        </p>
      )}
    </div>
  );
}
